/**
 * MAP
 *   role:      lib (old-browser fallback for the «UI nuova» CSS isolation)
 *   exports:   supportsCssLayers(), unwrapCssLayers(css, baseUrl), installCssLayerFallback(doc)
 *   uses:      fetch, MutationObserver (only on browsers without @layer)
 *   callers:   services/ui.js enable() (the per-app opt-in: medical, booking)
 *   tests:     tests/unit/lib/css-layer-fallback-test.js
 *   gotchas:   The apps that opt into the new UI compile their whole classic stylesheet inside
 *              `@layer classica { … }` (docs/ui-nuova/MIGRATION-PLAN.md §1.3). A browser without cascade
 *              layers (Safari < 15.4, Chrome/Edge < 99, Firefox < 97) drops every `@layer` block as an unknown
 *              at-rule, i.e. ALL classic CSS, and ignores `@import … layer(x)` (tenant custom_css). On those
 *              browsers this module re-serves the same CSS without the layer wrappers: each layered sheet is
 *              fetched, unwrapped and injected as a <style> right after its <link>. With layers gone the
 *              cascade is exactly the pre-layer classic one; the new UI is never offered there (ui.js gate).
 *              Modern browsers never reach any of this: one `typeof` check.
 */

/** @returns {boolean} true when the browser implements cascade layers */
function supportsCssLayers() {
  return typeof window !== 'undefined' && typeof window.CSSLayerBlockRule !== 'undefined';
}
const ABSOLUTE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/|#)/i;

/**
 * Remove every cascade-layer construct from a stylesheet, keeping the rules:
 * `@layer a, b;` statements are dropped, `@layer x { … }` / `@layer { … }` keep their body without the wrapper,
 * `@import url(…) layer(x) …;` loses its `layer(…)` part. Strings and comments are copied verbatim. When
 * `baseUrl` is given, relative `url(…)` references are made absolute (the text moves from the sheet's URL into a
 * <style> that resolves against the document).
 * @param {string} css
 * @param {string} [baseUrl]
 * @returns {string}
 */
function unwrapCssLayers(css, baseUrl) {
  const source = String(css ?? '');
  let out = '';
  const stack = []; // per open `{`: true = a dropped @layer wrapper
  let i = 0;
  const n = source.length;
  while (i < n) {
    const ch = source[i];
    if (ch === '/' && source[i + 1] === '*') {
      const end = source.indexOf('*/', i + 2);
      const stop = end === -1 ? n : end + 2;
      out += source.slice(i, stop);
      i = stop;
      continue;
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      while (j < n && source[j] !== ch) {
        j += source[j] === '\\' ? 2 : 1;
      }
      out += source.slice(i, j + 1);
      i = j + 1;
      continue;
    }
    if (ch === '@' && /^@layer(?![\w-])/i.test(source.slice(i, i + 7))) {
      const semi = source.indexOf(';', i);
      const brace = source.indexOf('{', i);
      if (brace !== -1 && (semi === -1 || brace < semi)) {
        stack.push(true);
        i = brace + 1;
      } else {
        i = semi === -1 ? n : semi + 1;
      }
      continue;
    }
    if (ch === '@' && /^@import(?![\w-])/i.test(source.slice(i, i + 8))) {
      const semi = source.indexOf(';', i);
      const stop = semi === -1 ? n : semi + 1;
      out += source.slice(i, stop).replace(/\s+layer(?:\([^)]*\))?(?=[\s;])/i, '');
      i = stop;
      continue;
    }
    if (ch === '{') {
      stack.push(false);
      out += ch;
      i++;
      continue;
    }
    if (ch === '}') {
      if (!stack.pop()) {
        out += ch;
      }
      i++;
      continue;
    }
    out += ch;
    i++;
  }
  return baseUrl ? absolutizeUrls(out, baseUrl) : out;
}
function absolutizeUrls(css, baseUrl) {
  return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (match, quote, ref) => {
    if (ABSOLUTE_URL.test(ref.trim())) {
      return match;
    }
    try {
      return `url(${quote}${new URL(ref.trim(), baseUrl).href}${quote})`;
    } catch {
      return match;
    }
  });
}
const DONE = 'bbLayerFallback';
function unwrapLink(link) {
  if (link.dataset[DONE] || !/(^|\s)stylesheet(\s|$)/i.test(link.rel || '') || !link.href) {
    return;
  }
  let href;
  try {
    href = new URL(link.href, document.baseURI);
  } catch {
    return;
  }
  if (href.origin !== window.location.origin) {
    return; // third-party sheets are not layered by our build, and CORS would refuse the read
  }
  link.dataset[DONE] = '1';
  window
  // Accept: a dev server (Vite) answers a plain fetch of a .css URL with a JS module wrapping it
  .fetch(href.href, {
    credentials: 'same-origin',
    headers: {
      Accept: 'text/css'
    }
  }).then(response => response.ok && /text\/css/i.test(response.headers.get('content-type') || '') ? response.text() : '').then(text => {
    if (!/@layer|layer\(/i.test(text) || !link.isConnected) {
      return;
    }
    const style = document.createElement('style');
    style.textContent = unwrapCssLayers(text, href.href);
    link.after(style);
    // the link's surviving (unlayered) rules are duplicated in the <style>: same order, so same cascade
    link.disabled = true;
  }).catch(() => {
    // offline / blocked: the page keeps whatever the browser could parse
  });
}

// No done-flag: a template-rendered <style> (BbClassicStyle) can change its text later. The write happens only when
// unwrapping changes the text, so the observer's own write (or a comment that merely mentions @layer) cannot loop.
function unwrapStyle(style) {
  const text = style.textContent || '';
  if (!/@layer|layer\(/i.test(text)) {
    return;
  }
  const unwrapped = unwrapCssLayers(text, document.baseURI);
  if (unwrapped !== text) {
    style.textContent = unwrapped;
  }
}
function visit(node) {
  if (node.nodeType !== 1) {
    return;
  }
  if (node.tagName === 'LINK') {
    unwrapLink(node);
  } else if (node.tagName === 'STYLE') {
    unwrapStyle(node);
  } else {
    node.querySelectorAll?.('link[rel~="stylesheet"], style').forEach(visit);
  }
}
let installed = false;

/**
 * On a browser without cascade layers, unwrap every layered sheet now and every one added later (lazy route
 * chunks, BbClassicStyle blocks). No-op elsewhere, and idempotent.
 * @param {Document} [doc]
 * @returns {boolean} true when the fallback is active
 */
function installCssLayerFallback(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || supportsCssLayers()) {
    return false;
  }
  if (installed) {
    return true;
  }
  installed = true;
  doc.querySelectorAll('link[rel~="stylesheet"], style').forEach(visit);
  if (typeof window.MutationObserver === 'function') {
    new window.MutationObserver(records => {
      for (const record of records) {
        record.addedNodes.forEach(visit);
        if (record.type === 'characterData' || record.type === 'childList') {
          const parent = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          if (parent?.tagName === 'STYLE') {
            unwrapStyle(parent);
          }
        }
      }
    }).observe(doc.documentElement, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }
  return true;
}

export { installCssLayerFallback, supportsCssLayers, unwrapCssLayers };
//# sourceMappingURL=css-layer-fallback.js.map
